import LibraryIcon from './LibraryIcon';
import { isLibraryIconName } from '../../utils/libraryIcons';

/**
 * Choice elements, as antd Select options — one renderer, every surface.
 *
 * WHICH elements: a Choice with a list, and nothing else (`rendersAsChoice`).
 * Choices live only on choice elements (the owner's ruling, 2026-10-19; KPE-033).
 * `options.choices` SURVIVES a change of type — the card editor keeps the list
 * so a change back to Choice can reuse it — and on any other type it is inert:
 * a short text, number, rating or date that carries one keeps its own text box,
 * `>` and `<`, or picker. (Until KPR-1015 a short text with a list read as a
 * choice, the legacy construct the migration KPR-1012 converted away.)
 *
 * A choice may carry an `icon` (an export name from components/icons) and a
 * `color`; the item form renders both, so the automation builders do too — a
 * status the user knows as a red dot must not become a bare word here.
 */

// The predicates are pure and live in utils/choiceValues (KPR-142), so the list
// filter builder's model can ask the same question; every component keeps
// importing them from here.
export { elementHasChoices, rendersAsChoice } from '../../utils/choiceValues';

const choiceIcon = choice => {
	if ( choice?.iconJSX ) return choice.iconJSX;
	// A library icon, drawn on demand (KPR-1187); nothing for a name the library does not have.
	return isLibraryIconName( choice?.icon ) ? <LibraryIcon name={ choice.icon } /> : null;
};

/**
 * A card def's `options.choices` -> Select options. Labels are ReactNodes, so
 * each carries `searchText` for the platform's optionSearchFilter (antd's own
 * filter stringifies the value and would match nothing).
 */
export function buildChoiceOptions( choices = [] ) {
	return ( Array.isArray( choices ) ? choices : [] ).map( choice => {
		const value = choice?.value ?? choice?.label;
		const text = choice?.label ?? `${ choice?.value ?? '' }`;
		const icon = choiceIcon( choice );
		return {
			value,
			label: (
				<span className='choice-option'>
					{choice?.color ? (
						<span className='choice-option__swatch' style={{ backgroundColor: choice.color }} />
					) : null}
					{icon ? <span className='choice-option__icon'>{icon}</span> : null}
					<span className='choice-option__label'>{text}</span>
				</span>
			),
			searchText: `${ text }`,
		};
	} );
}

export default buildChoiceOptions;
